import { enableProdMode, APP_INITIALIZER, importProvidersFrom } from '@angular/core';
import { environment } from './environments/environment';

import 'froala-editor/js/plugins/align.min.js'; // align plugin
import 'froala-editor/js/plugins/colors.min.js'; // color plugin
import 'froala-editor/js/plugins/file.min.js'; // file plugin
import 'froala-editor/js/plugins/font_family.min.js'; // fontFamily plugin
import 'froala-editor/js/plugins/font_size.min.js'; // fontSize plugin
import 'froala-editor/js/plugins/lists.min.js'; // lists plugin
import 'froala-editor/js/plugins/save.min.js'; // save plugin
import 'froala-editor/js/plugins/print.min.js'; // print plugin
import 'froala-editor/js/plugins/word_paste.min.js'; // wordPaste plugin
import 'froala-editor/js/plugins/image.min.js'; // image plugin
import 'froala-editor/js/plugins/draggable.min.js'; // draggable plugin

import './favicon-script';
// import { webViewerInternalOpts, webViewerInternalElement } from './app/core/common';
import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import { PlatformModule } from '@angular/cdk/platform';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { CalendarCommonModule, DateAdapter, CalendarWeekModule } from 'angular-calendar';
import { MatBadgeModule } from '@angular/material/badge';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSortModule } from '@angular/material/sort';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatNativeDateModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
// import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { NgxPaginationModule } from 'ngx-pagination';
import { NgxSliderModule } from 'ngx-slider-v2';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatTableModule } from '@angular/material/table';
import { ToastrModule } from 'ngx-toastr';
import { CoreModule } from './app/core/core.module';
import { routes } from './app/app.routes';
import { provideAnimations } from '@angular/platform-browser/animations';
import { NgxSpinnerModule } from 'ngx-spinner';
import { DatePipe } from '@angular/common';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { CustomInterceptor, OneDriveInterceptor } from './app/utils/interceptors';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { FroalaEditorHelpers } from 'src/models/FroalaEditorHelpers';
import { GapiSession } from './app/services/cloud-storage/google-drive/gapi.session';
import { ReturnMimetypePipe } from './app/utils/pipes/return-mimetype.pipe';
import { RemoveExtensionStringPipe } from './app/utils/pipes/remove-extension-string.pipe';
import { ArrayToStringPipe } from './app/utils/pipes/array-to-string.pipe';
import { registerLocaleData } from '@angular/common';
// import WebViewer, {WebViewerInstance} from "@pdftron/webviewer";

// needed for the Time Scheduler
// https://github.com/mattlewis92/angular-calendar/issues/431
import localeNl from '@angular/common/locales/nl';
import localeFr from '@angular/common/locales/fr';
import localeDe from '@angular/common/locales/de';
import { PreloadAllModules, provideRouter, withPreloading } from '@angular/router';
import { EPubAnnotationServiceToken } from './app/services/epub/base-epub-annotation.service';
import { EpubBookmarkService } from './app/services/epub/epub-bookmark.service';
import { EpubHighlightService } from './app/services/epub/epub-highlight.service';
import { EpubCommentService } from './app/services/epub/epub-comment.service';
// import { Core } from '@pdftron/webviewer';
registerLocaleData(localeNl);
registerLocaleData(localeFr);
registerLocaleData(localeDe);

if (environment.production) {
  enableProdMode();
}

// WebViewer(webViewerInternalOpts, webViewerInternalElement)
//   .then(() => platformBrowserDynamic().bootstrapModule(AppModule))

const NewTooltipDefaultOptions: MatTooltipDefaultOptions = {
  showDelay: 0,
  hideDelay: 0,
  touchGestures: 'auto',
  position: 'below',
  touchendHideDelay: 0,
  disableTooltipInteractivity: true,
}

function initGapi(gapiSession: GapiSession) {
  return () => gapiSession.handleClientLoad();
}

// Fire-and-forget warmup of the Dictionary API so the first user-triggered request
// (open the lookup dialog) doesn't pay the Container Apps cold-start tax. /health is
// anonymous and exercises the DB and Blob health checks, which is what we want to warm.
function warmupDictionaryApi() {
  return () => {
    try {
      fetch(`${environment.connectedApiUrls.dictionaryApi}/health`, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-store',
      }).catch(() => { /* best-effort */ });
    } catch { /* best-effort */ }
    return Promise.resolve();
  };
}

bootstrapApplication(AppComponent, {
    providers: [
        provideRouter(routes, withPreloading(PreloadAllModules)),
        importProvidersFrom(NgxSpinnerModule, CoreModule, ToastrModule.forRoot({
            positionClass: 'toast-bottom-right',
            autoDismiss: false,
            preventDuplicates: true,
            countDuplicates: true,
            maxOpened: 0,
            timeOut: 2000
        }), MatTableModule, MatButtonToggleModule, NgxSliderModule, NgxPaginationModule, MatMenuModule, MatDialogModule, 
          // NgbModule, 
          MatInputModule, MatNativeDateModule, MatPaginatorModule, MatSortModule, MatProgressSpinnerModule, MatFormFieldModule, MatBadgeModule, CalendarCommonModule.forRoot({
            provide: DateAdapter,
            useFactory: adapterFactory,
        }), CalendarWeekModule, PlatformModule),
        ArrayToStringPipe,
        RemoveExtensionStringPipe,
        ReturnMimetypePipe,
        { provide: APP_INITIALIZER, useFactory: initGapi, deps: [GapiSession], multi: true },
        { provide: APP_INITIALIZER, useFactory: () => FroalaEditorHelpers.applyScreenSizeHelper, multi: true },
        { provide: APP_INITIALIZER, useFactory: warmupDictionaryApi, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: CustomInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: OneDriveInterceptor, multi: true },
        { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: NewTooltipDefaultOptions },
        { provide: EPubAnnotationServiceToken, useClass: EpubHighlightService, multi: true },
        { provide: EPubAnnotationServiceToken, useClass: EpubBookmarkService, multi: true },
        { provide: EPubAnnotationServiceToken, useClass: EpubCommentService, multi: true },
        DatePipe,
        provideAnimations()
    ]
})
  // .then(() => WebViewer(webViewerInternalOpts, webViewerInternalElement))
  .then(() => (window as any).Core.setWorkerPath('/lib/pdftron/core'))
  // .then((instance: WebViewerInstance) => {
  //   instance.Core.setWorkerPath('/lib/pdftron/core');
  //   instance.Core.PDFNet.initialize();
  // })
  .catch(err => console.error(err));

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    for (const registration of registrations) {
      registration.unregister();
    }
  });
}

if ('caches' in window) {
  caches.keys().then(keyList => {
    return Promise.all(keyList.map(key => {
      return caches.delete(key);
    }));
  });
}

if (!(File as any).prototype.arrayBuffer) {
  (File as any).prototype.arrayBuffer = function (): Promise<ArrayBuffer> {
    return new Promise<ArrayBuffer>((resolve) => {
      const fileReader = new FileReader();
      fileReader.onload = (event) => {
        const data = (event.target as any).result as ArrayBuffer;
        resolve(data);
      };

      fileReader.readAsArrayBuffer(this);
    });
  };
}
